import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import FundoEvento from '@/components/feature/FundoEvento';
import Logo from '@/components/ui/Logo';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import Cartao from '@/components/ui/Cartao';
import Carregando from '@/components/ui/Carregando';
import CartaoTalao from './components/CartaoTalao';
import type { RespostaBusca, TalaoEncontrado } from './types';
import { cpfCompleto, dataCompleta, dataParaIso, mascararData, somenteDigitos } from './utils';

const DURACAO_AVISO_MS = 4000;

// Página pública (linguagem FESTA): fundo com a arte da campanha desfocada,
// cartões de vidro e título branco. A pessoa digita CPF + nascimento e recebe
// o(s) talão(ões) dela. Nada de CPF ou data vai para URL, storage, cookie ou console.
export default function BuscarTalaoPage() {
  const [cpf, setCpf] = useState('');
  const [nascimento, setNascimento] = useState('');

  const [erroCpf, setErroCpf] = useState<string | null>(null);
  const [erroData, setErroData] = useState<string | null>(null);
  const [erroMsg, setErroMsg] = useState<string | null>(null);
  const [erroRede, setErroRede] = useState<string | null>(null);

  const [buscando, setBuscando] = useState(false);
  const [bloqueado, setBloqueado] = useState(false);
  const [taloes, setTaloes] = useState<TalaoEncontrado[] | null>(null);

  const [aviso, setAviso] = useState<string | null>(null);
  const avisoTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    },
    [],
  );

  function avisar(msg: string) {
    setAviso(msg);
    if (avisoTimer.current) window.clearTimeout(avisoTimer.current);
    avisoTimer.current = window.setTimeout(() => setAviso(null), DURACAO_AVISO_MS);
  }

  const podeBuscar = cpfCompleto(cpf) && dataCompleta(nascimento) && !buscando && !bloqueado;

  async function buscar() {
    setErroCpf(null);
    setErroData(null);
    setErroMsg(null);
    setErroRede(null);

    const cpfDigitos = somenteDigitos(cpf);
    const iso = dataParaIso(nascimento);

    if (cpfDigitos.length !== 11) {
      setErroCpf('Digite os 11 números do CPF.');
      return;
    }
    if (!iso) {
      setErroData('Confira a data de nascimento.');
      return;
    }

    setBuscando(true);
    try {
      const { data, error } = await supabase.rpc('talao_por_cpf', {
        p_cpf: cpfDigitos,
        p_nascimento: iso,
      });
      if (error) throw error;

      const resposta = (data as RespostaBusca | null) ?? null;
      if (resposta?.ok && resposta.taloes && resposta.taloes.length > 0) {
        setTaloes(resposta.taloes);
        return;
      }

      setErroMsg(resposta?.mensagem || 'Não encontramos um talão com esses dados.');
      if (resposta?.motivo === 'bloqueado') setBloqueado(true);
    } catch (e) {
      setErroRede(mensagemAmigavel(e, 'Não consegui buscar agora. Tente de novo.'));
    } finally {
      setBuscando(false);
    }
  }

  function buscarOutro() {
    setTaloes(null);
    setCpf('');
    setNascimento('');
    setErroCpf(null);
    setErroData(null);
    setErroMsg(null);
    setErroRede(null);
    setBloqueado(false);
  }

  const mostrandoResultado = !!taloes && taloes.length > 0;
  const arteFundo = mostrandoResultado ? taloes?.[0]?.arte_url ?? null : null;

  return (
    <FundoEvento imagemUrl={arteFundo}>
      {/* Aviso flutuante (toast) */}
      {aviso && (
        <div className="animate-subir fixed left-1/2 top-20 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-2xl border border-success-300 bg-success-50 px-4 py-3 text-sm font-semibold text-success-800 shadow-sm">
            <span className="flex h-5 w-5 items-center justify-center text-success-600">
              <i className="ri-check-double-line" />
            </span>
            {aviso}
          </div>
        </div>
      )}

      {/* Logo direto sobre o fundo, centrado (sem barra branca) */}
      <header className="pt-6">
        <div className="mx-auto flex w-full max-w-[560px] justify-center px-4">
          <Logo />
        </div>
      </header>

      <main className="mx-auto w-full max-w-[560px] px-4 pb-16 pt-6 md:pt-10">
        {mostrandoResultado ? (
          <>
            <div className="animate-subir text-center">
              <h1 className="text-2xl font-extrabold text-white">Achamos o seu talão! 🎉</h1>
              <p className="mt-2 text-[15px] font-medium text-white/80">
                Deus te abençoe e boas vendas!
              </p>
            </div>

            <div className="mt-6 space-y-4">
              {taloes!.map((t, i) => (
                <CartaoTalao key={t.token || i} talao={t} aoAvisar={avisar} />
              ))}
            </div>

            <Botao
              variante="secundario"
              icone="ri-arrow-left-line"
              className="mt-5 w-full"
              onClick={buscarOutro}
            >
              Buscar outro talão
            </Botao>
          </>
        ) : (
          <>
            <div className="animate-subir text-center">
              <h1 className="text-2xl font-extrabold text-white">Pegue o seu talão</h1>
              <p className="mt-2 text-[15px] font-medium text-white/80">
                Digite o CPF e a data de nascimento do campista.
              </p>
            </div>

            <Cartao variante="vidro" className="animate-subir mt-6 p-6 md:p-7">
              {erroMsg && (
                <div className="mb-5 flex items-start gap-2 rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm font-medium text-warning-800">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                    <i className="ri-alert-line" />
                  </span>
                  <span>{erroMsg}</span>
                </div>
              )}

              <div className="space-y-5">
                <Campo
                  id="cpf"
                  name="cpf"
                  rotulo="CPF do campista"
                  placeholder="000.000.000-00"
                  mascara="cpf"
                  inputMode="numeric"
                  autoComplete="off"
                  value={cpf}
                  erro={erroCpf}
                  onChange={(v) => {
                    setCpf(v);
                    if (erroCpf) setErroCpf(null);
                  }}
                />

                <Campo
                  id="nascimento"
                  name="nascimento"
                  rotulo="Data de nascimento"
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  autoComplete="off"
                  value={nascimento}
                  erro={erroData}
                  onChange={(v) => {
                    setNascimento(mascararData(v));
                    if (erroData) setErroData(null);
                  }}
                />
              </div>

              <Botao
                tamanho="lg"
                icone="ri-search-line"
                carregando={buscando}
                disabled={!podeBuscar}
                className="mt-6 w-full"
                onClick={buscar}
              >
                {buscando ? 'Buscando…' : 'Buscar meu talão'}
              </Botao>

              {erroRede && (
                <div className="mt-4 rounded-2xl border border-danger-200 bg-danger-50 px-4 py-3 text-center">
                  <p className="text-sm font-medium text-danger-700">{erroRede}</p>
                  <Botao
                    variante="secundario"
                    icone="ri-refresh-line"
                    className="mt-3"
                    disabled={buscando}
                    onClick={buscar}
                  >
                    Tentar de novo
                  </Botao>
                </div>
              )}
            </Cartao>

            {buscando && <Carregando cartoes={1} className="mt-4" />}

            <p className="mt-5 text-center text-[14px] font-medium text-white/80">
              Não encontrou? Fale com a direção.
            </p>
          </>
        )}
      </main>
    </FundoEvento>
  );
}